// ============================================================
// CEO data layer — talks to the CRM SaaS ("company portal") project,
// NOT to the property-management backend the other portals use.
//
// The company portal is a Supabase app (crm-saas) whose CEO View lives at
// /portal/ceo. Rather than re-implement its tables here, the mobile app
// consumes ONE aggregated endpoint that returns the same figures:
//
//   POST {CRM_URL}/functions/v1/ceo-summary   -> CeoSummary
//
// AUTH — two ways in, no second password to remember:
//
//  1. SSO (the normal path). The CEO is already signed into Asset Desk, so we
//     exchange that session for a short-lived, single-purpose token
//     (POST {ASSET_DESK_API}/ceo/sso-token) and send it to the CRM. The CRM
//     verifies the signature with a shared secret and matches the email
//     against company_shareholders.
//
//  2. Email code (the fallback). Corporate-services clients aren't necessarily
//     Asset Desk investors, so they have no session to exchange. For them the
//     CRM issues and verifies a 6-digit code itself — it's the side that knows
//     which emails are company owners — and returns a token of the same shape.
//
// Until the CRM functions are deployed (see docs/ceo-portal-integration.md),
// every call fails fast and CEOPortal renders mock preview data instead.
// ============================================================

// Baked in rather than left to env vars: .env.* is gitignored, so an env-only
// setup silently loses these on any other build machine and the CEO tab quietly
// disappears. Same approach as PORTAL_API and the main app's API_URL. The anon
// key is a public client key by design — it only gets a caller past the API
// gateway; the CEO functions do their own auth on our signed token.
const CRM_URL_DEFAULT = 'https://efzvajrgrqpguzleejba.supabase.co';
const CRM_ANON_DEFAULT =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVmenZhanJncnFwZ3V6bGVlamJhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODI5MDEwNjcsImV4cCI6MjA5ODQ3NzA2N30._p6Rf3G0s4AW-AWReAhQgxwAAuFCK4O9p5Xn-kA-sfw';

export const CRM_SUPABASE_URL = (process.env.REACT_APP_CRM_SUPABASE_URL || CRM_URL_DEFAULT).replace(/\/+$/, '');
export const CRM_SUPABASE_ANON_KEY = process.env.REACT_APP_CRM_SUPABASE_ANON_KEY || CRM_ANON_DEFAULT;

// Same default as the main app (see InvestorPortal.tsx) so the CEO tab talks to
// whichever backend the rest of the app is already using.
const ASSET_DESK_API =
  (process.env.REACT_APP_API_URL || 'https://investmenter-backend-1081814658115.europe-west1.run.app') + '/api';

// True once the CRM project is wired up — otherwise we stay in preview mode.
export const ceoApiConfigured = (): boolean => !!(CRM_SUPABASE_URL && CRM_SUPABASE_ANON_KEY);

// The app's existing Asset Desk session, if the user is signed in.
export function assetDeskToken(): string | null {
  try {
    return localStorage.getItem('authToken') || localStorage.getItem('token');
  } catch {
    return null;
  }
}

// ---------------- Response contract ----------------
// Mirrors crm-saas/src/pages/portal/PortalCEO.tsx. Money is in `currency` and
// NET of VAT unless the field name says otherwise.

export interface CeoMonthPoint {
  month: string;      // 'Jan' … 'Dec'
  revenue: number;
  expenses: number;
}

export interface CeoOverdueRow {
  customer: string;
  amount: number;         // open balance, VAT inclusive
  dueDate: string | null; // ISO date
  invoiceNo?: string;
}

export interface CeoRankRow {
  name: string;
  amount: number;
}

export interface CeoExpiry {
  label: string;         // 'Trade licence' / holder name
  date: string | null;   // ISO date
  days: number | null;   // whole days from today, negative = expired
}

export interface CeoVat {
  periodLabel: string;   // e.g. 'Q3 2026'
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  outputVat: number;
  inputVat: number;
  netVat: number;        // output - input; positive = payable
}

export interface CeoBankAccount {
  name: string;
  balance: number;
  currency: string;      // may differ from the company's base currency
  asOf: string | null;   // balance date as stated by the client
  updatedAt: string | null; // when the record was actually edited
}

export interface CeoBank {
  total: number;         // AED accounts only — NOT FX-converted across currencies
  currency: string;      // 'AED'
  asOf: string | null;   // latest client-stated balance date
  lastUpdated: string | null; // ISO timestamp of the most recent balance edit
  accounts: CeoBankAccount[];
}

export interface CeoSummary {
  company: {
    id: string;
    name: string;
    jurisdiction?: string;
    licenceNumber?: string;
    trn?: string;
  };
  currency: string;      // 'AED'
  year: number;
  revenueYtd: number;
  expensesYtd: number;
  profitYtd: number;
  marginPct: number;
  receivableTotal: number;
  overdueTotal: number;
  overdueCount: number;
  overdue: CeoOverdueRow[];       // top 5, largest balance first
  spendYtdInclVat: number;
  vat: CeoVat | null;
  monthly: CeoMonthPoint[];       // 12 rows, Jan..Dec of `year`
  topCustomers: CeoRankRow[];     // top 5 by revenue
  topSuppliers: CeoRankRow[];     // top 5 by spend
  compliance: {
    licence: CeoExpiry | null;
    visas: CeoExpiry[];           // renewals due within 90 days
  };
  // Optional on purpose: the field is additive, so a CEO token served by an
  // older function revision returns a payload without it. The UI hides the
  // card when it's absent or has no accounts rather than crashing.
  bank?: CeoBank;
  source?: 'live' | 'mock';
}

// Error codes the CRM returns, surfaced to the user as sentences.
export const CEO_ERRORS: Record<string, string> = {
  not_owner: 'This account isn’t listed as a company owner. Ask your account manager to add you in the company portal.',
  no_company: 'No company is linked to this account yet. Please contact your account manager.',
  unauthorized: 'Your session has expired. Please sign in again.',
  not_registered: 'We don’t recognise that email. Use the address your company portal account was created with.',
  bad_code: 'Incorrect or expired code.',
};

const humanise = (code: string): string => CEO_ERRORS[code] || code;

// ---------------- Transport ----------------

const TIMEOUT_MS = 8000;

async function withTimeout(url: string, init: RequestInit): Promise<any> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(url, { ...init, signal: controller.signal });
    const body: any = await res.json().catch(() => null);
    if (!res.ok) {
      const code = body?.error || `HTTP ${res.status}`;
      // Carry the raw code alongside the sentence: callers need to distinguish
      // "you aren't an owner" (a dead end — no code can be sent either) from a
      // transient failure where retrying or signing in again makes sense.
      const err: Error & { code?: string } = new Error(humanise(code));
      err.code = code;
      throw err;
    }
    return body;
  } finally {
    clearTimeout(timer);
  }
}

// Calls a CRM edge function. `token` is a CEO session token from either path.
async function crmFn<T>(name: string, payload: unknown, token?: string): Promise<T> {
  if (!ceoApiConfigured()) throw new Error('The company portal is not configured on this build');
  return withTimeout(`${CRM_SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    body: JSON.stringify(payload ?? {}),
    headers: {
      'Content-Type': 'application/json',
      apikey: CRM_SUPABASE_ANON_KEY,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  }) as Promise<T>;
}

// ---------------- Path 1: SSO from the Asset Desk session ----------------

// Exchanges the app's session for a 15-minute portal token. Throws if the user
// isn't signed into Asset Desk or the server has SSO disabled.
export async function ceoSsoToken(): Promise<string> {
  const session = assetDeskToken();
  if (!session) throw new Error('Not signed in to Asset Desk');
  const body = await withTimeout(`${ASSET_DESK_API}/ceo/sso-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session}` },
    body: '{}',
  });
  if (!body?.token) throw new Error('Could not obtain a portal token');
  return body.token as string;
}

// ---------------- Path 2: email code, issued by the CRM ----------------

export async function ceoRequestCode(email: string): Promise<void> {
  await crmFn('ceo-request-code', { email: email.trim().toLowerCase() });
}

export async function ceoVerifyCode(email: string, code: string): Promise<string> {
  const body = await crmFn<{ token?: string }>('ceo-verify-code', {
    email: email.trim().toLowerCase(),
    code: code.trim(),
  });
  if (!body?.token) throw new Error('Sign-in failed');
  return body.token;
}

// ---------------- The read ----------------

// One aggregated read for the whole CEO screen. The function resolves the
// caller's company and enforces owner-only access itself, so the app never has
// to know the schema.
export async function ceoSummary(token: string): Promise<CeoSummary> {
  const data = await crmFn<CeoSummary>('ceo-summary', {}, token);
  return { ...data, source: 'live' };
}

// Convenience: the whole SSO path in one call, for the silent attempt on mount.
export async function ceoSummaryViaSso(): Promise<CeoSummary> {
  return ceoSummary(await ceoSsoToken());
}
